import React, { useState, useEffect } from "react";
import "./styles.css";
import NumberFlow, { NumberFlowGroup } from "@number-flow/react";
import continuous from "@number-flow/react";
import { Footer, useFooterState } from "./components/footer";
import {
  useSmoothedEpochTime,
  useCopyShortcut,
  useTimeFormatCycling,
  formatTime
} from "./hooks";

const COPY_SUCCESS_MESSAGE = "Copied to clipboard";

export default function App() {
  // Use the enhanced epoch time hook
  const smoothedEpochTime = useSmoothedEpochTime(0.05, 0);
  const epochTime = Math.round(smoothedEpochTime);

  const { currentFormat, cycleFormat } = useTimeFormatCycling();

  const formattedTime = React.useMemo(() => {
    return formatTime(epochTime, currentFormat);
  }, [epochTime, currentFormat]);

  const [displayText, setDisplayText] = useState<string>('');

  const { footerState, showToast, resetToast } = useFooterState();

  useEffect(() => {
    if (currentFormat !== 'Epoch Time (ms)') {
      setDisplayText(formattedTime.display as string);
    }
  }, [formattedTime, currentFormat]);

  const handleCopy = React.useCallback(() => {
    const textToCopy = formattedTime.display.toString();
    navigator.clipboard.writeText(textToCopy);
    showToast(COPY_SUCCESS_MESSAGE, "success");
    setTimeout(() => resetToast(), 2000);
  }, [formattedTime, showToast, resetToast]);

  useCopyShortcut(handleCopy);

  const footerMessage = `${formattedTime.formatLabel}`;

  return (
    <div className="App">
      {currentFormat === 'Epoch Time (ms)' ? (
        <NumberFlowGroup>
          <NumberFlow
            value={epochTime}
            format={{
              notation: "standard",
              maximumFractionDigits: 0,
              useGrouping: false, // Remove commas as requested
            }}
            plugins={[continuous as any]}
            transformTiming={{
              duration: 800,
              easing: "cubic-bezier(0.4, 0.0, 0.2, 1)"
            }}
            spinTiming={{
              duration: 800,
              easing: "cubic-bezier(0.4, 0.0, 0.2, 1)"
            }}
            opacityTiming={{
              duration: 400,
              easing: "ease-out"
            }}
            willChange={true}
            style={{
              fontVariantNumeric: "tabular-nums",
            }}
          />
        </NumberFlowGroup>
      ) : (
        // Display formatted time for other formats
        <div className="time-display">
          {displayText}
        </div>
      )}

      <Footer
        footerState={footerState}
        defaultMessage={footerMessage}
      />
    </div>
  );
}